/**
 * Edge-to-edge swipeable gallery for the detail screen.
 *
 * Uses a real spring (not a CSS transition) so a fling settles the way the design does, and
 * drives the transform imperatively to keep the drag off React's render path. Falls back to a
 * single static pane when a prompt has one example or none.
 */

import { useCallback, useEffect, useRef, useState } from 'react';
import type { ExampleMedia } from '@promptvault/shared';
import { useI18n } from '../lib/i18n';
import { Lightbox } from './Lightbox';
import { Icon } from './Icon';
import { Media } from './Media';

const STIFFNESS = 170;
const DAMPING = 26;
const PROJECTION = 0.28;

export function Gallery({
  seed,
  examples,
  alt,
  height = 400,
  blurNsfw = false,
}: {
  seed: string;
  examples: ExampleMedia[];
  alt: string;
  height?: number;
  blurNsfw?: boolean;
}) {
  const track = useRef<HTMLDivElement>(null);
  const frame = useRef<HTMLDivElement>(null);
  const raf = useRef<number>();
  const drag = useRef<{
    x0: number;
    base: number;
    cur: number;
    last: number;
    t: number;
    v: number;
    active: boolean;
  } | null>(null);
  const [index, setIndex] = useState(0);
  const [revealed, setRevealed] = useState(false);
  const [width, setWidth] = useState(0);
  const [fullscreen, setFullscreen] = useState(false);
  const { t, rtl } = useI18n();

  // Panes are sized from the actual frame, so the gallery works at 320px and at 430px.
  useEffect(() => {
    const el = frame.current;
    if (!el) return;
    const measure = () => setWidth(el.clientWidth);
    measure();
    const ro = new ResizeObserver(measure);
    ro.observe(el);
    return () => ro.disconnect();
  }, []);

  useEffect(() => () => cancelAnimationFrame(raf.current!), []);

  // A resize mid-swipe would otherwise leave the track parked between panes.
  useEffect(() => {
    if (track.current && width) {
      track.current.style.transform = `translate3d(${-index * width}px, 0, 0)`;
    }
  }, [width, index]);

  const panes = examples.length > 0 ? examples : [null];
  const count = panes.length;

  const springTo = useCallback((from: number, to: number, v0: number) => {
    const el = track.current;
    if (!el) return;
    cancelAnimationFrame(raf.current!);
    let x = from;
    let vel = v0;
    let last = performance.now();
    const tick = (now: number) => {
      const dt = Math.min(0.032, (now - last) / 1000);
      last = now;
      vel += (-STIFFNESS * (x - to) - DAMPING * vel) * dt;
      x += vel * dt;
      if (Math.abs(x - to) < 0.4 && Math.abs(vel) < 14) {
        el.style.transform = `translate3d(${to}px, 0, 0)`;
        return;
      }
      el.style.transform = `translate3d(${x}px, 0, 0)`;
      raf.current = requestAnimationFrame(tick);
    };
    raf.current = requestAnimationFrame(tick);
  }, []);

  const onPointerDown = useCallback(
    (e: React.PointerEvent) => {
      /**
       * Tracked even for a single pane, because the tap and the swipe are the same gesture here.
       *
       * This returned early on `count < 2`, so `drag.current` was never set, `onPointerUp`
       * returned at its own guard, and the `< 6px` branch that opens fullscreen could not run —
       * on every prompt with exactly one example, which is most of the library. The ⤢ button
       * still worked, so the feature was reachable and the picture simply ignored taps.
       */
      if (!width) return;
      cancelAnimationFrame(raf.current!);
      try {
        e.currentTarget.setPointerCapture(e.pointerId);
      } catch {
        /* pointer capture unsupported */
      }
      const base = -index * width;
      drag.current = {
        x0: e.clientX,
        base,
        cur: base,
        last: e.clientX,
        t: performance.now(),
        v: 0,
        active: true,
      };
    },
    [count, index, width],
  );

  const onPointerMove = useCallback(
    (e: React.PointerEvent) => {
      const d = drag.current;
      if (!d?.active || !width) return;
      // One pane has nothing to slide to; the pointer is still tracked so the tap can resolve.
      if (count < 2) return;
      const min = -(count - 1) * width;
      let cur = d.base + (e.clientX - d.x0);
      // Rubber-band past both ends.
      if (cur > 0) cur *= 0.32;
      if (cur < min) cur = min + (cur - min) * 0.32;
      const now = performance.now();
      const dt = now - d.t;
      if (dt > 4) {
        d.v = ((e.clientX - d.last) / dt) * 1000;
        d.t = now;
        d.last = e.clientX;
      }
      d.cur = cur;
      if (track.current) track.current.style.transform = `translate3d(${cur}px, 0, 0)`;
    },
    [count, width],
  );

  const onPointerUp = useCallback(
    (e: React.PointerEvent) => {
      const d = drag.current;
      if (!d?.active || !width) return;
      d.active = false;
      if (count > 1) {
        const projected = d.cur + d.v * PROJECTION;
        const next = Math.max(0, Math.min(count - 1, Math.round(-projected / width)));
        springTo(d.cur, -next * width, d.v);
        if (next !== index) setIndex(next);
      }

      /**
       * A tap that never became a swipe opens fullscreen.
       *
       * Threshold rather than a separate onClick: with a swipeable track, a click fires at the
       * end of every drag, so tapping and swiping would be the same event. 6px is below the
       * noise floor of a stationary finger and well under any intended swipe.
       */
      if (Math.abs(e.clientX - d.x0) < 6 && examples.length > 0 && (!blurNsfw || revealed)) {
        setFullscreen(true);
      }
    },
    [blurNsfw, count, examples.length, index, revealed, springTo, width],
  );

  return (
    <div
      ref={frame}
      dir="ltr"
      className="relative overflow-hidden bg-media-bg"
      style={{ height, touchAction: 'pan-y', cursor: count > 1 ? 'grab' : 'default' }}
      onPointerDown={onPointerDown}
      onPointerMove={onPointerMove}
      onPointerUp={onPointerUp}
      onPointerCancel={onPointerUp}
    >
      <div ref={track} className="flex h-full" style={{ willChange: 'transform' }}>
        {panes.map((ex, i) => (
          <div key={ex?.id ?? i} className="h-full flex-none" style={{ width: width || '100%' }}>
            <Media
              seed={seed}
              example={ex}
              variant={i}
              ratio="auto"
              alt={i === 0 ? alt : ''}
              className="h-full w-full"
              priority={i === 0}
              blurred={blurNsfw && !revealed}
              onReveal={() => setRevealed(true)}
            />
          </div>
        ))}
      </div>

      {count > 1 && (
        <>
          <div className="pointer-events-none absolute inset-x-0 bottom-3.5 flex items-center justify-center gap-1.5">
            {panes.map((_, i) => (
              <span
                key={i}
                className="h-1.5 rounded-full transition-[width,background] duration-200 ease-pv"
                style={{
                  width: i === index ? 18 : 6,
                  background: i === index ? 'var(--pv-on-media)' : 'rgb(255 255 255 / 0.5)',
                }}
              />
            ))}
          </div>
          {/*
            `dir` restored on the overlay, because the frame forces `ltr`.
            
            The frame has to: the track is positioned by transform arithmetic that inverts under
            `rtl`. But that also pins every `end-*` overlay to the right, so on a Farsi detail
            screen the counter and the fullscreen button sat in the opposite corner from the
            favourite heart, the video duration and every other trailing control in the app —
            the one place the mirroring stopped, and only because of an implementation detail
            of the swipe.
          */}
          <div
            dir={rtl ? 'rtl' : 'ltr'}
            className="pointer-events-none absolute inset-x-0 top-3 flex justify-end px-3"
          >
            {/* `dir` back to ltr on the text itself: "1 / 3" is a fraction, not a sentence. */}
            <span
              dir="ltr"
              className="tnum rounded-full px-2 py-0.5 text-caption font-medium text-on-media"
              style={{ background: 'var(--pv-media-scrim)' }}
            >
              {index + 1} / {count}
            </span>
          </div>
        </>
      )}

      {/*
        A visible affordance as well as the tap target. "Tap the picture" is not discoverable,
        and it is the only route to the full-size example on a library whose whole argument is
        the example. Keyboard-reachable, which the tap gesture is not.
      */}
      {examples.length > 0 && (!blurNsfw || revealed) && (
        <button
          type="button"
          aria-label={t.viewFullscreen}
          onPointerDown={(e) => e.stopPropagation()}
          onClick={() => setFullscreen(true)}
          className={`absolute bottom-3 flex h-9 w-9 items-center justify-center rounded-full text-on-media transition-opacity duration-fast ease-pv ${
            rtl ? 'left-3' : 'right-3'
          }`}
          style={{ background: 'var(--pv-media-scrim)' }}
        >
          <Icon name="expand" size={16} />
        </button>
      )}

      {fullscreen && (
        <Lightbox
          examples={examples}
          startIndex={index}
          alt={alt}
          onClose={() => setFullscreen(false)}
        />
      )}
    </div>
  );
}
